import { DOCUMENT } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from '@layout/header/header.component';
import { LiveMarqueeComponent } from '@layout/live-marquee/live-marquee.component';
import { NavigationComponent } from '@layout/navigation/navigation.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HlmToasterComponent } from '@spartan-ng/helm/sonner';
import { ERoute } from '@core/routes';
import { BalanceFacade } from '@feature/balance/facade';
import { AuthFacade } from '@feature/auth/facade';
import { FirstDepositBannerService } from '@infrastructure/first-deposit-banner';

@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, HeaderComponent, LiveMarqueeComponent, NavigationComponent, HlmToasterComponent],
  templateUrl: './layout.component.html',
})
export class LayoutComponent implements OnInit {
  public showLive = signal(true);
  public isMobile = true;
  public showLiveOnlyOnPc = signal(false);

  #excludedRoutes = ['profile', 'roulette', 'jellyfish', 'leaders', 'upgrade', 'rocket'];
  #externalBalanceModalSessionKey = 'pg_external_balance_modal_seen_v1';

  readonly #document = inject(DOCUMENT);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #balanceFacade = inject(BalanceFacade);
  readonly #authFacade = inject(AuthFacade);
  readonly #firstDepositBanner = inject(FirstDepositBannerService);

  protected readonly balanceVm = this.#balanceFacade.vm;
  protected readonly authVm = this.#authFacade.vm;
  protected readonly isHomeRoute = signal(false);
  protected readonly externalBalanceModalVisible = signal(false);
  readonly #externalBalanceModalShownThisSession = signal(this.#isExternalBalanceModalSeenInSession());
  protected readonly showExternalBalanceModal = computed(() => this.externalBalanceModalVisible());
  protected readonly firstDepositBannerPercent = computed(() => {
    const percent = Number(this.authVm().user?.first_deposit_banner?.bonus_percent);
    return percent === 30 ? 30 : 20;
  });
  protected readonly firstDepositBannerImageUrl = computed(() => {
    return `/images/deposit-banner/deposit-banner-${this.firstDepositBannerPercent()}.png`;
  });
  protected readonly firstDepositBannerText = computed(() => {
    return `Забери +${this.firstDepositBannerPercent()}% к первому депозиту`;
  });
  protected readonly showFirstDepositBanner = computed(() => {
    const banner = this.authVm().user?.first_deposit_banner;

    return (
      this.isHomeRoute() &&
      banner?.show === true &&
      !this.#firstDepositBanner.dismissedThisLaunch() &&
      !this.#firstDepositBanner.depositCompletedThisLaunch()
    );
  });

  constructor() {
    effect(() => {
      const isHomeRoute = this.isHomeRoute();
      const isFirstExternalBalance = this.balanceVm().isFirstExternalBalance === true;
      const isShownInSession = this.#externalBalanceModalShownThisSession();

      if (!isHomeRoute && this.externalBalanceModalVisible()) {
        this.externalBalanceModalVisible.set(false);
        return;
      }

      if (isHomeRoute && isFirstExternalBalance && !isShownInSession) {
        this.externalBalanceModalVisible.set(true);
        this.#externalBalanceModalShownThisSession.set(true);
        this.#setExternalBalanceModalSeenInSession();
      }
    });
  }

  ngOnInit(): void {
    this.isMobile = window?.innerWidth < 768;
    this.#syncRouteFlags(this.#getCleanUrl());
    this.#router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
      next: event => {
        if (event instanceof NavigationEnd) {
          this.#syncRouteFlags(event.urlAfterRedirects.split('?')[0].split('#')[0] || '');
        }
      },
    });
  }

  protected closeExternalBalanceModal(): void {
    this.externalBalanceModalVisible.set(false);
  }

  protected openFirstDepositBanner(event?: Event): void {
    event?.stopPropagation();
    this.#router.navigate(['/', ERoute.Profile], {
      queryParams: {
        openBalance: true,
      },
    });
  }

  protected dismissFirstDepositBanner(event: Event): void {
    event.stopPropagation();
    this.#firstDepositBanner.dismiss();
  }

  #isHomeUrl(cleanUrl: string): boolean {
    return cleanUrl === '/' || cleanUrl === '' || cleanUrl === `/${ERoute.Home}`;
  }

  #getCleanUrl(): string {
    return this.#router.url.split('?')[0].split('#')[0] || '';
  }

  #syncRouteFlags(cleanUrl: string): void {
    const showLive = !this.#excludedRoutes.some(route => cleanUrl.includes(route));
    this.showLive.set(showLive);
    this.showLiveOnlyOnPc.set(this.isMobile && showLive);
    this.isHomeRoute.set(this.#isHomeUrl(cleanUrl));
  }

  #isExternalBalanceModalSeenInSession(): boolean {
    const view = this.#document.defaultView;
    if (!view) {
      return false;
    }

    try {
      return view.sessionStorage.getItem(this.#externalBalanceModalSessionKey) === '1';
    } catch {
      return false;
    }
  }

  #setExternalBalanceModalSeenInSession(): void {
    const view = this.#document.defaultView;
    if (!view) {
      return;
    }

    try {
      view.sessionStorage.setItem(this.#externalBalanceModalSessionKey, '1');
    } catch {
      // ignore storage errors
    }
  }
}
